import { getInstrumentId, isDefined, ListedInstrument, useInstrumentStoreItem } from '@eih-netbroker/common';
import BigNumber from 'bignumber.js';
import { useEffect, useEffectEvent, useMemo, useState } from 'react';
import { instrumentStore } from '../../../../shared/data/instrumentStore';
import { useEStream } from '../../../../shared/services/sse/useEStream';
import { useInstrumentSearch } from '../../../../shared/services/useInstrumentSearch';

export type SuggestedPrices = {
  bestBid: number | undefined;
  bestAsk: number | undefined;
  midPrice: number | undefined;
  lastPrice: number | undefined;
  tickSize: number | undefined;
};

export const useSuggestedPrices = (instrument: ListedInstrument | null | undefined): SuggestedPrices => {
  const { subscribeToInstrumentChanges } = useEStream();
  const { getTickSizeForInstrumentAndPrice } = useInstrumentSearch();
  const [tickSize, setTickSize] = useState<number | undefined>(undefined);
  const instrumentId = instrument ? getInstrumentId(instrument) : undefined;

  const instrumentStoreItem = useInstrumentStoreItem(
    instrument ?? null,
    instrumentStore,
    subscribeToInstrumentChanges,
    {
      source: 'SuggestedPrices',
    },
  );

  const bestBid = useMemo(() => {
    if (!instrumentStoreItem?.bestBid || instrumentStoreItem.bestBid.isMarketPrice) return undefined;
    return instrumentStoreItem.bestBid.value;
  }, [instrumentStoreItem?.bestBid]);

  const bestAsk = useMemo(() => {
    if (!instrumentStoreItem?.bestAsk || instrumentStoreItem.bestAsk.isMarketPrice) return undefined;
    return instrumentStoreItem.bestAsk.value;
  }, [instrumentStoreItem?.bestAsk]);

  const midPrice = useMemo(() => {
    if (!isDefined(bestBid) || !isDefined(bestAsk) || !isDefined(tickSize) || tickSize <= 0) {
      return undefined;
    }

    const bnBid = new BigNumber(bestBid);
    const bnAsk = new BigNumber(bestAsk);
    const bnTick = new BigNumber(tickSize);

    if (!bnTick.isFinite() || bnTick.lte(0) || !bnBid.isFinite() || !bnAsk.isFinite()) {
      return undefined;
    }

    const spread = bnAsk.minus(bnBid);
    if (spread.lte(0)) return undefined;

    const halfSpreadInTicks = spread.div(bnTick).div(2).integerValue(BigNumber.ROUND_FLOOR);
    const shift = halfSpreadInTicks.multipliedBy(bnTick);
    const candidate = bnBid.plus(shift);

    if (!candidate.isEqualTo(bnBid) && !candidate.isEqualTo(bnAsk)) {
      return candidate.toNumber();
    }

    const candidatePlusTick = candidate.plus(bnTick);
    if (candidate.isEqualTo(bnBid) && candidatePlusTick.isLessThan(bnAsk)) {
      return candidatePlusTick.toNumber();
    }

    return undefined;
  }, [bestBid, bestAsk, tickSize]);

  const lastPrice = instrumentStoreItem?.price?.value;
  const suggestedPrices = useMemo<SuggestedPrices>(
    () => ({ bestBid, bestAsk, midPrice, lastPrice, tickSize }),
    [bestBid, bestAsk, midPrice, lastPrice, tickSize],
  );

  const loadTickSize = useEffectEvent((isCancelled: () => boolean): void => {
    setTickSize(undefined);
    if (!instrument || !isDefined(bestBid)) return;

    getTickSizeForInstrumentAndPrice(instrument, bestBid).then(nextTickSize => {
      if (!isCancelled()) setTickSize(nextTickSize);
    });
  });

  useEffect(() => {
    let cancelled = false;
    loadTickSize(() => cancelled);

    return () => {
      cancelled = true;
    };
  }, [instrumentId, bestBid]);

  return suggestedPrices;
};
